/**
 * Message search filters (Discord's syntax):
 *
 *   from:sara  in:#general  has:image  has:link  "exact words"
 *
 * A name or channel after a filter can be in any script. Names match
 * usernames, display names and nicknames after the protocol's search
 * normalisation (`normalizeForSearch`: Arabic-script letter forms,
 * diacritics, ZWNJ, digits), so a name typed on an Arabic keyboard finds the
 * same name typed on another layout. The server normalises the text part
 * the same way.
 */
import {
  ChannelType,
  isVoiceChannelType,
  normalizeForSearch,
  type MessageSearchQuery,
  type Snowflake,
} from '@neosocial/protocol';
import type { DataState } from '../store/reducer';
import { channelsInSidebarOrder, displayName } from '../store/selectors';

export type SearchFilter = 'from' | 'in' | 'has';

export interface ParsedSearch {
  /** The free text, filters removed. */
  text: string;
  from: string[];
  in: string[];
  has: string[];
}

const KEYWORDS: Record<string, SearchFilter> = {
  from: 'from',
  in: 'in',
  has: 'has',
};

const HAS_VALUES: Record<string, 'image' | 'link'> = {
  image: 'image',
  img: 'image',
  images: 'image',
  file: 'image',
  link: 'link',
  links: 'link',
  url: 'link',
};

/** Splits the input into words, keeping "quoted phrases" and filter:"quoted values" whole. */
export function splitWords(input: string): string[] {
  const out: string[] = [];
  const re = /(\S+?:)?"([^"]*)"?|\S+/gu;
  for (const m of input.matchAll(re)) {
    if (m[2] !== undefined) out.push((m[1] ?? '') + (m[1] ? m[2] : `"${m[2]}"`));
    else out.push(m[0]);
  }
  return out;
}

export function parseSearch(input: string): ParsedSearch {
  const p: ParsedSearch = { text: '', from: [], in: [], has: [] };
  const text: string[] = [];
  for (const w of splitWords(input)) {
    // a keyword may be written with the full-width colon too
    const m = /^([^:：]+)[:：](.*)$/u.exec(w);
    const kw = m ? KEYWORDS[normalizeForSearch(m[1]!)] : undefined;
    if (m && kw && m[2]) p[kw].push(m[2]);
    else text.push(w.replace(/^"(.*)"$/u, '$1'));
  }
  p.text = text.join(' ').trim();
  return p;
}

export interface ResolvedSearch {
  query: MessageSearchQuery;
  /** Filters that matched nothing: shown to the person, never silently dropped. */
  unknown: Array<{ filter: SearchFilter; value: string }>;
  /** True when there is something to search for. */
  ready: boolean;
}

function matchMember(d: DataState, guildId: Snowflake, value: string): Snowflake | null {
  const mention = /^<@!?(\d+)>$/.exec(value);
  if (mention) return mention[1]!;
  const q = normalizeForSearch(value.replace(/^@/, ''));
  if (!q) return null;
  const members = Object.values(d.members[guildId] ?? {});
  const names = (id: Snowflake) => {
    const m = d.members[guildId]?.[id];
    const u = d.users[id] ?? m?.user;
    return [u?.username, u?.global_name, m?.nick, displayName(u, m)]
      .filter((x): x is string => !!x)
      .map(normalizeForSearch);
  };
  const exact = members.filter((m) => names(m.user.id).includes(q));
  if (exact.length === 1) return exact[0]!.user.id;
  const prefix = members.filter((m) => names(m.user.id).some((n) => n.startsWith(q)));
  return prefix.length === 1 ? prefix[0]!.user.id : (exact[0]?.user.id ?? null);
}

function matchChannel(d: DataState, guildId: Snowflake, value: string): Snowflake | null {
  const mention = /^<#(\d+)>$/.exec(value);
  if (mention) return mention[1]!;
  const q = normalizeForSearch(value.replace(/^#/, ''));
  if (!q) return null;
  // text, voice and stage channels: each has its own chat; in the
  // sidebar's order, so of two with one name the first there wins
  const list = channelsInSidebarOrder(d, guildId).filter(
    (c) => c.type === ChannelType.GUILD_TEXT || isVoiceChannelType(c.type),
  );
  const exact = list.filter((c) => normalizeForSearch(c.name ?? '') === q);
  if (exact.length) return exact[0]!.id;
  const prefix = list.filter((c) => normalizeForSearch(c.name ?? '').startsWith(q));
  return prefix.length === 1 ? prefix[0]!.id : null;
}

/** Turns parsed filters into the API query, resolving names against what READY sent. */
export function resolveSearch(
  d: DataState,
  guildId: Snowflake,
  p: ParsedSearch,
  limit = 25,
  offset = 0,
): ResolvedSearch {
  const query: MessageSearchQuery = { limit, offset };
  const unknown: ResolvedSearch['unknown'] = [];
  if (p.text) query.content = p.text;
  const from = p.from[0];
  if (from) {
    const id = matchMember(d, guildId, from);
    if (id) query.author_id = id;
    else unknown.push({ filter: 'from', value: from });
  }
  const inn = p.in[0];
  if (inn) {
    const id = matchChannel(d, guildId, inn);
    if (id) query.channel_id = id;
    else unknown.push({ filter: 'in', value: inn });
  }
  const has = p.has[0];
  if (has) {
    const v = HAS_VALUES[normalizeForSearch(has)];
    if (v) query.has = v;
    else unknown.push({ filter: 'has', value: has });
  }
  const ready =
    !unknown.length && !!(query.content || query.author_id || query.channel_id || query.has);
  return { query, unknown, ready };
}
